import { captureException, captureMessage } from '@/lib/sentry';

const isDevelopment = import.meta.env.DEV;

interface LogOptions {
  prefix?: string;
  color?: string;
}

export const logger = {
  log: (_message: any, ..._args: any[]) => {
    // No console.log in production
  },
  
  error: (message: any, ...args: any[]) => {
    if (isDevelopment) {
      console.error('[ERROR]', message, ...args);
    } else {
      // Production: send to Sentry only, no console output
      try {
        const err = message instanceof Error ? message : new Error(String(message));
        captureException(err, { args: args.length ? JSON.stringify(args).substring(0, 500) : undefined });
      } catch {
        // Sentry not initialized, silent fail
      }
    }
  },
  
  warn: (message: any, ...args: any[]) => {
    if (isDevelopment) {
      console.warn('[WARN]', message, ...args);
    } else {
      try {
        captureMessage(String(message), 'warning');
      } catch {
        // silent
      }
    }
  },
  
  info: (message: any, ...args: any[]) => {
    if (isDevelopment) {
      console.info('[INFO]', message, ...args);
    }
  },
  
  debug: (message: any, ...args: any[]) => {
    if (isDevelopment) {
      console.debug('[DEBUG]', message, ...args);
    }
  },

  critical: (message: any, ...args: any[]) => {
    if (isDevelopment) {
      console.error('[CRITICAL]', message, ...args);
    } else {
      try {
        const err = message instanceof Error ? message : new Error(String(message));
        captureException(err, { level: 'fatal', args: args.length ? JSON.stringify(args).substring(0, 500) : undefined });
      } catch {
        // silent
      }
    }
  },

  secureLog: (level: 'info' | 'warn' | 'error', message: string, data?: any) => {
    const sanitizedData = data ? sanitizeLogData(data) : undefined;
    
    if (isDevelopment) {
      const timestamp = new Date().toISOString();
      switch (level) {
        case 'error':
          console.error(`[${timestamp}] [SECURE-ERROR]`, message, sanitizedData);
          break;
        case 'warn':
          console.warn(`[${timestamp}] [SECURE-WARN]`, message, sanitizedData);
          break;
        default:
          console.info(`[${timestamp}] [SECURE-INFO]`, message, sanitizedData);
      }
    } else if (level === 'error') {
      try {
        captureMessage(`[SECURE] ${message}`, 'error');
      } catch { /* silent */ }
    }
  },

  prefixed: (options: LogOptions) => ({
    log: (message: any, ...args: any[]) => {
      if (isDevelopment) {
        const prefix = options.prefix ? `[${options.prefix}]` : '';
        if (options.color) {
          console.log(`%c${prefix} ${message}`, `color: ${options.color}`, ...args);
        } else {
          console.log(prefix, message, ...args);
        }
      }
    },
    error: (message: any, ...args: any[]) => {
      if (isDevelopment) {
        const prefix = options.prefix ? `[${options.prefix}]` : '';
        console.error(prefix, message, ...args);
      }
    }
  })
};

// Function to sanitize sensitive data from logs
const sanitizeLogData = (data: any): any => {
  if (typeof data === 'string') {
    return data
      .replace(/password[^=]*=[^&\s]*/gi, 'password=[REDACTED]')
      .replace(/token[^=]*=[^&\s]*/gi, 'token=[REDACTED]')
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]')
      .replace(/\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g, '[CARD_REDACTED]');
  }
  
  if (typeof data === 'object' && data !== null) {
    const sanitized: any = Array.isArray(data) ? [] : {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (/password|token|secret|key|auth/i.test(key)) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = sanitizeLogData(data[key]);
        }
      }
    }
    return sanitized;
  }
  
  return data;
};

// Specialized loggers
export const authLogger = {
  log: (message: any, ...args: any[]) => logger.secureLog('info', `[AUTH] ${message}`, args),
  error: (message: any, ...args: any[]) => logger.secureLog('error', `[AUTH] ${message}`, args)
};

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

export const shouldLog = (level: LogLevel): boolean => {
  if (isDevelopment) return true;
  return ['warn', 'error', 'critical'].includes(level);
};

export const securityLogger = {
  debug: (message: string, data?: any) => {
    if (isDevelopment) {
      console.debug(`[SECURITY-DEBUG] ${message}`, data || '');
    }
  },
  log: (message: string, data?: any) => {
    if (isDevelopment) {
      logger.secureLog('info', `[SECURITY] ${message}`, data);
    }
  },
  warn: (message: string, data?: any) => {
    logger.secureLog('warn', `[SECURITY] ${message}`, data);
  },
  error: (message: string, data?: any) => {
    logger.secureLog('error', `[SECURITY] ${message}`, data);
  },
  critical: (message: string, data?: any) => {
    if (isDevelopment) {
      console.error(`[SECURITY-CRITICAL] 🚨 ${message}`, data || '');
    } else {
      try {
        captureMessage(`[SECURITY-CRITICAL] ${message}`, 'fatal');
      } catch { /* silent */ }
    }
  }
};

export const apiLogger = {
  log: (message: any, ...args: any[]) => logger.secureLog('info', `[API] ${message}`, args),
  error: (message: any, ...args: any[]) => logger.secureLog('error', `[API] ${message}`, args)
};

export const uiLogger = {
  log: (message: any, ...args: any[]) => logger.secureLog('info', `[UI] ${message}`, args),
  error: (message: any, ...args: any[]) => logger.secureLog('error', `[UI] ${message}`, args)
};
